import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../Revenue/revenue.css";
import { getMarketRevenue, getMarketRevenueStores, updateDefaultCommission, updateStoreCommission } from "./marketMoneyApi";
import { errorMessage } from "../../Revenue/revenueApi";
import { Avatar, Kpi, Loading, Pager, RangeBar, RevenueChart, SplitBar } from "../../Revenue/parts";
import { fmt } from "../../Revenue/format";

const usd = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const money = (v, cur) => `${cur || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`.trim();

const WALLET = {
  ok: { label: "OK", cls: "rv-badge-green" },
  grace: { label: "Owes (grace)", cls: "rv-badge-gold" },
  deactivated: { label: "Deactivated", cls: "rv-badge-red" },
};

/// Market → Overview: where the marketplace money goes. Sales (GMV) split
/// into the app's commission and the sellers' share, what sellers already
/// paid back and what they still owe — per currency, per store, for any
/// date range — and the commission rates themselves.
export default function MarketMoney() {
  const [range, setRange] = useState({ from: "", to: "", preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getMarketRevenue({ from: range.from, to: range.to }));
    } catch (e) {
      setError(errorMessage(e, "Could not load market revenue"));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const u = data?.usd;
  const rateNote = u && u.gmv > 0 ? `${Math.round((u.app_commission / u.gmv) * 1000) / 10}% effective` : "";

  return (
    <div className="rv-wrap">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h2 className="rv-title">Market revenue</h2>
          <p className="rv-sub">
            Cash on delivery: the seller collects the money and owes the app its commission on every delivered order.
          </p>
        </div>
        <RangeBar value={range} onChange={setRange} />
      </div>

      {error && <div className="rv-error mb-3">{error}</div>}
      {loading && !data ? (
        <Loading />
      ) : data ? (
        <>
          <div className="rv-kpis mb-3">
            <Kpi
              tone="gold"
              label="Sales (delivered)"
              value={usd(u.gmv)}
              foot={
                <>
                  <b>{fmt(data.orders.delivered)}</b> orders · avg <b>{usd(u.avg_order)}</b> · {fmt(data.stores.selling)} stores sold
                </>
              }
            />
            <Kpi
              tone="violet"
              label="App commission"
              value={usd(u.app_commission)}
              foot={
                <>
                  {rateNote} · default <b>{data.rates.default_rate}%</b>
                </>
              }
            />
            <Kpi tone="pink" label="Sellers' share" value={usd(u.sellers_share)} foot="What the stores keep" />
            <Kpi tone="green" label="Commission collected" value={usd(u.collected)} foot="Paid back by sellers in this range" />
            <Kpi
              tone="grey"
              label="Owed right now"
              value={usd(u.outstanding)}
              foot={
                <>
                  <b>{fmt(data.stores.owing)}</b> stores with a negative wallet
                </>
              }
            />
            <Kpi
              label="Orders placed"
              value={fmt(data.orders.placed)}
              foot={
                <>
                  <b>{fmt(data.orders.by_status?.cancelled || 0)}</b> cancelled · <b>{fmt(data.orders.by_status?.declined || 0)}</b> declined ·{" "}
                  <b>{fmt(data.orders.by_status?.return_to_seller || 0)}</b> returned
                </>
              }
            />
          </div>
          {u.unconverted_currencies?.length > 0 && (
            <div className="rv-error mb-3">
              No exchange rate for {u.unconverted_currencies.join(", ")} — those amounts are left out of the $ totals (see the per-currency table).
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <div className="rv-panel h-100">
                <div className="rv-panel-title">Split of sales</div>
                <SplitBar
                  parts={[
                    { label: "Sellers' share", value: u.sellers_share, color: "#fc155c" },
                    { label: "App commission", value: u.app_commission, color: "#7437d0" },
                  ]}
                />
                <div className="rv-panel-title mt-4">
                  Trend <span className="text-grey fs-6">(≈ USD, {data.timeseries.bucket === "day" ? "per day" : "per month"})</span>
                </div>
                <RevenueChart
                  points={data.timeseries.points}
                  keys={["sellers_share", "app_commission"]}
                  height={280}
                  emptyText="No sales in this range yet."
                />
              </div>
            </div>
            <div className="col-lg-4">
              <DefaultRate rates={data.rates} onSaved={load} />
            </div>
          </div>

          <div className="rv-panel mb-3">
            <div className="rv-panel-title">By currency</div>
            {!data.per_currency.length ? (
              <div className="rv-empty">No sales yet.</div>
            ) : (
              <div className="rv-table-wrap">
                <table className="rv-table">
                  <thead>
                    <tr>
                      <th>Currency</th>
                      <th className="rv-num">Delivered orders</th>
                      <th className="rv-num">Sales</th>
                      <th className="rv-num">App commission</th>
                      <th className="rv-num">Effective rate</th>
                      <th className="rv-num">Sellers' share</th>
                      <th className="rv-num">Collected</th>
                      <th className="rv-num">Owed now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_currency.map((c) => (
                      <tr key={c.currency}>
                        <td className="fw-bold">{c.currency}</td>
                        <td className="rv-num">{fmt(c.orders)}</td>
                        <td className="rv-num">{fmt(c.gmv)}</td>
                        <td className="rv-num">{fmt(c.app_commission)}</td>
                        <td className="rv-num">{c.effective_rate}%</td>
                        <td className="rv-num">{fmt(c.sellers_share)}</td>
                        <td className="rv-num">{fmt(c.collected)}</td>
                        <td className="rv-num">{fmt(c.outstanding)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="row g-3 mb-3">
            <StatusCard
              title="Orders placed in range"
              total={data.orders.placed}
              rows={["pending", "ready_to_ship", "shipped", "delivered", "declined", "return_to_seller", "cancelled"].map((k) => [
                k,
                data.orders.by_status?.[k] || 0,
              ])}
            />
            <StatusCard
              title="Stores — approval"
              total={data.stores.total}
              rows={["pending", "approved", "declined"].map((k) => [k, data.stores.by_approval?.[k] || 0])}
            />
            <StatusCard
              title="Stores — commission wallet"
              total={data.stores.total}
              rows={[
                ["ok", data.stores.by_wallet?.ok || 0],
                ["owes (grace)", data.stores.by_wallet?.grace || 0],
                ["deactivated", data.stores.by_wallet?.deactivated || 0],
              ]}
            />
          </div>
        </>
      ) : null}

      <StoresTable range={range} defaultRate={data?.rates?.default_rate} />
    </div>
  );
}

function StatusCard({ title, total, rows }) {
  return (
    <div className="col-lg-4">
      <div className="rv-panel h-100">
        <div className="rv-panel-title mb-1">{title}</div>
        <div className="rv-kpi-value mb-2">{fmt(total)}</div>
        {rows.map(([k, v]) => (
          <div key={k} className="rv-stat-row">
            <span className="text-capitalize">{k.replaceAll("_", " ")}</span>
            <span>{fmt(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DefaultRate({ rates, onSaved }) {
  const [rate, setRate] = useState(String(rates.default_rate));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => setRate(String(rates.default_rate)), [rates.default_rate]);

  const save = async (e) => {
    e.preventDefault();
    const v = parseFloat(rate);
    if (!(v >= 0 && v <= 100)) {
      setMsg({ ok: false, text: "Enter a percentage between 0 and 100." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await updateDefaultCommission(v);
      setMsg({ ok: true, text: "Saved. Applies to orders delivered from now on." });
      onSaved && onSaved();
    } catch (err) {
      setMsg({ ok: false, text: errorMessage(err, "Could not save") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rv-panel h-100" onSubmit={save}>
      <div className="rv-panel-title">Commission rate</div>
      <label className="rv-label">Default for every store (%)</label>
      <input className="rv-input mb-2" type="number" min="0" max="100" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
      <p className="rv-sub mb-3">
        {rates.custom_rate_stores > 0
          ? `${rates.custom_rate_stores} store(s) have their own rate — edit them in the table below.`
          : "No store has its own rate."}
        {rates.default_is_fallback ? " (Not saved yet — the built-in fallback is used.)" : ""}
      </p>
      <button className="rv-btn violet w-100" disabled={busy}>
        {busy ? "Saving…" : "Save default"}
      </button>
      {msg && <div className={`mt-3 ${msg.ok ? "rv-success" : "rv-error"}`}>{msg.text}</div>}
      <p className="rv-sub mt-3 mb-0">Changing a rate never re-prices commissions already charged.</p>
    </form>
  );
}

function StoresTable({ range, defaultRate }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState("gmv");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // store id
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [range.from, range.to, sort, query]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMarketRevenueStores({ from: range.from, to: range.to, sort, q: query, page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch(() => alive && setRes({ data: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, sort, query, page, reloadKey]);

  const saveRate = async (storeId, value) => {
    setSaving(true);
    try {
      await updateStoreCommission(storeId, value === "" ? null : parseFloat(value));
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch {
      // keep the editor open; the value stays for another try
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rv-panel">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="rv-panel-title mb-0">Stores</div>
        <div className="d-flex gap-2">
          <input className="rv-input" style={{ maxWidth: 220 }} placeholder="Search store…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="rv-select" style={{ maxWidth: 220 }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="gmv">Sort: sales</option>
            <option value="app_commission">Sort: app commission</option>
            <option value="outstanding">Sort: owed now</option>
            <option value="collected">Sort: collected</option>
            <option value="orders">Sort: delivered orders</option>
          </select>
        </div>
      </div>
      {loading && !res ? (
        <Loading />
      ) : !res?.data?.length ? (
        <div className="rv-empty">No stores.</div>
      ) : (
        <>
          <div className="rv-table-wrap">
            <table className="rv-table" style={{ minWidth: 1000 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Store</th>
                  <th>Commission</th>
                  <th className="rv-num">Delivered</th>
                  <th className="rv-num">Sales</th>
                  <th className="rv-num">App</th>
                  <th className="rv-num">Seller</th>
                  <th className="rv-num">Collected</th>
                  <th className="rv-num">Owed now</th>
                  <th>Wallet</th>
                </tr>
              </thead>
              <tbody>
                {res.data.map((r) => {
                  const s = r.store;
                  const w = WALLET[r.wallet_status] || WALLET.ok;
                  return (
                    <tr key={s.id} className="rv-click" onClick={() => navigate(`/dashboard/market/store-requests/${s.id}`)}>
                      <td className="text-grey">{r.rank}</td>
                      <td>
                        <div className="rv-user">
                          <Avatar img={s.logo_url} name={s.name} />
                          <div style={{ minWidth: 0 }}>
                            <div className="rv-user-name">{s.name}</div>
                            <div className="rv-user-sub">
                              {s.currency}
                              {s.approval_status !== "approved" ? ` · ${s.approval_status}` : ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                        {editing === s.id ? (
                          <div className="d-flex gap-1 align-items-center">
                            <input
                              className="rv-input"
                              style={{ width: 80, padding: "0.3rem 0.5rem" }}
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder={`${defaultRate ?? ""}`}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              autoFocus
                            />
                            <button className="rv-btn violet" style={{ padding: "0.3rem 0.6rem" }} disabled={saving} onClick={() => saveRate(s.id, editValue)}>
                              Save
                            </button>
                            <button className="rv-btn ghost" style={{ padding: "0.3rem 0.6rem" }} disabled={saving} onClick={() => setEditing(null)}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span
                            role="button"
                            title="Click to change (empty = default)"
                            onClick={() => {
                              setEditing(s.id);
                              setEditValue(r.custom_rate ? String(r.commission_rate) : "");
                            }}
                          >
                            <b>{r.commission_rate}%</b>{" "}
                            <span className={`rv-badge ${r.custom_rate ? "rv-badge-violet" : "rv-badge-grey"}`}>
                              {r.custom_rate ? "custom" : "default"}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="rv-num">
                        {fmt(r.orders_delivered)}
                        <span className="text-grey small"> / {fmt(r.orders_placed)}</span>
                      </td>
                      <td className="rv-num fw-bold">{money(r.gmv, s.currency)}</td>
                      <td className="rv-num">{money(r.app_commission, s.currency)}</td>
                      <td className="rv-num">{money(r.sellers_share, s.currency)}</td>
                      <td className="rv-num">{money(r.collected, s.currency)}</td>
                      <td className="rv-num">{r.outstanding > 0 ? <span className="rv-amount-neg">{money(r.outstanding, s.currency)}</span> : "—"}</td>
                      <td>
                        <span className={`rv-badge ${w.cls}`}>{w.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager meta={res.meta} onPage={setPage} />
        </>
      )}
    </div>
  );
}
